/**
 * Claim normalisation across identity providers.
 *
 * The app reads five things off the user object: roles, orgId, the IdP's own org id,
 * per-user permission grants, and a "bounce" flag for free-trial signups. Auth0 and
 * Keycloak deliver those under different claim names:
 *
 *   Auth0     https://www.chaostrack.com/roles        (set by the id-token-setup Action)
 *   Keycloak  ct_allowed_roles                        (set by a protocol mapper)
 *
 * The Auth0 names are URL-shaped, and Keycloak treats "." inside a claim name as a
 * NESTING separator — so emitting `https://www.chaostrack.com/roles` from Keycloak
 * would need backslash escaping that is easy to get subtly wrong and hard to test.
 * Normalising on the client instead keeps the difference in one small file.
 *
 * Every reader below accepts EITHER shape, so a single build works against either
 * provider and a half-migrated environment cannot produce a user with no roles.
 */

const AUTH0_NS = "https://www.chaostrack.com"

const firstDefined = (...values) => values.find((v) => v !== undefined && v !== null)

/** Roles as an array, from either provider. */
export const claimRoles = (user) => {
  const value = firstDefined(user?.[`${AUTH0_NS}/roles`], user?.ct_allowed_roles)
  if (Array.isArray(value)) return value
  // Keycloak can emit a single-element multivalued claim as a bare string.
  if (typeof value === "string" && value.length > 0) return [value]
  return []
}

/** ChaosTrack organization id (ORG_NODE.id), from either provider. */
export const claimOrgId = (user) => firstDefined(user?.[`${AUTH0_NS}/orgId`], user?.ct_org_id) ?? ""

/**
 * The IdP's OWN organization identifier.
 *
 * On Auth0 this is the `org_xxx` id used for org-scoped login. Keycloak Organizations
 * are not used yet (orgs are modelled as a user attribute, matching how
 * app_metadata.orgId works today), so this is empty there and callers that use it to
 * pass org context to /authorize simply skip it.
 */
export const claimIdpOrgId = (user) => firstDefined(user?.[`${AUTH0_NS}/auth0OrgId`], user?.ct_idp_org_id) ?? ""

/**
 * Per-user feature grants as an array of `feature:action:scope` strings.
 *
 * Auth0's id-token Action emits a JSON ARRAY. The Keycloak mapper emits a
 * COMMA-JOINED STRING, deliberately: Hasura session variables are strings and the
 * server splits x-hasura-permissions on commas, so keeping the same shape on both
 * sides of the token avoids a second parsing rule. Accept both here.
 */
export const claimPermissions = (user) => {
  const value = firstDefined(user?.[`${AUTH0_NS}/permissions`], user?.ct_permissions)
  if (Array.isArray(value)) return value
  if (typeof value === "string") {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

/** Free-trial bounce flag. Auth0-only concept; absent on Keycloak. */
export const claimBounce = (user) => Boolean(user?.[`${AUTH0_NS}/bounce`])

/**
 * Whether this subject authenticated on the passwordless/OTP plane rather than with
 * a password.
 *
 * Auth0 encodes it in the subject prefix (`email|...` vs `auth0|...`). Keycloak
 * subjects are bare UUIDs with no such signal, so the server stamps an `authPlane`
 * user attribute instead, surfaced as the `ct_auth_plane` claim. Players who signed
 * in through the server-minted OTP plane carry `authPlane: 'otp'`.
 *
 * Callers use this only for presentation (e.g. hiding password-management UI). It is
 * NEVER an authorisation input — roles are.
 */
export const claimIsPasswordlessUser = (user) => {
  if (typeof user?.sub === "string" && user.sub.startsWith("email|")) return true
  if (user?.ct_auth_plane === "otp" || user?.authPlane === "otp") return true
  return false
}

/**
 * How this subject authenticated, as a UI label.
 *
 * Auth0 encodes the method in the subject PREFIX (`auth0|` = database password,
 * `email|` = passwordless, anything else = an enterprise/social connection). Keycloak
 * subjects are bare UUIDs with no such signal, so the prefix test silently reports
 * "SSO" for every Keycloak user — including ordinary password logins.
 *
 * Resolution order:
 *   1. an explicit auth-plane claim (the server stamps `otp` for email-code sessions);
 *   2. the Auth0 subject prefixes, for Auth0 environments;
 *   3. Keycloak's `identity_provider` claim, present only when the login was brokered
 *      through an external IdP;
 *   4. otherwise a local password login.
 *
 * Presentation only. Authorisation is always decided by roles.
 */
export const authMethodLabel = (user) => {
  const sub = user?.sub
  if (!sub) return "—"
  if (claimIsPasswordlessUser(user)) return "Passwordless"
  if (sub.startsWith("auth0|")) return "Password"
  if (sub.includes("|")) return "SSO"
  if (user?.identity_provider) return "SSO"
  return "Password"
}

/** True when the session came through an external identity provider. */
export const isSsoUser = (user) => authMethodLabel(user) === "SSO"
